import { useState, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getProject } from '../api/projects'
import { getElectricalPanelById } from '../api/electricalPanels'
import { getSpecificationItem } from '../api/specification'
import { getDeviceContacts } from '../api/deviceContacts'
import DeviceInstances from './DeviceInstances'
import CableInstances from './CableInstances'
import DeviceSpecEditModal from './modals/DeviceSpecEditModal'
import PostsSection from './Posts/PostsSection'
import SpecificationDocumentsSection from './SpecificationDocumentsSection'
import { useAuth } from '../AuthContext'
import { PermissionRow } from '../api/admin'
import { apiClient } from '../api/client'
import { getCommInterfaceTypes } from '../api/commInterfaceTypes'
import {
  getSpecificationInterfaces,
  createSpecificationInterface,
  updateSpecificationInterface,
  deleteSpecificationInterface,
  type SpecificationInterface
} from '../api/specificationInterfaces'

export default function DeviceViewPage() {
  const { projectId, panelId, deviceId } = useParams<{ projectId: string; panelId?: string; deviceId: string }>()
  const [showEditModal, setShowEditModal] = useState(false)
  const { user: currentUser } = useAuth()

  // Загрузка прав пользователя на проект
  const { data: projectAccessData } = useQuery({
    queryKey: ['my-project-access', currentUser?.id],
    queryFn: async () => {
      if (!currentUser?.id) return { access: [] }
      const response = await apiClient.get('/api/auth/my-project-access')
      return response.data as { access: Array<{ project_id: number; role_in_project: string }> }
    },
    enabled: !!currentUser?.id && currentUser.role !== 'system_admin',
  })

  // Загрузка всех permissions для текущего пользователя и проекта
  const { data: permissionsData } = useQuery({
    queryKey: ['my-permissions', currentUser?.id, projectId],
    queryFn: async () => {
      if (!currentUser?.id || !projectId) return { permissions: [] }
      const response = await apiClient.get('/api/auth/my-permissions', {
        params: { project_id: Number(projectId) },
      })
      return response.data as { permissions: PermissionRow[] }
    },
    enabled: !!currentUser?.id && !!projectId && currentUser.role !== 'system_admin',
  })

  const allPermissions: PermissionRow[] = permissionsData?.permissions ?? []

  // Проверка, может ли пользователь редактировать (не только просмотр)
  const canEdit = useMemo(() => {
    if (currentUser?.role === 'system_admin') return true
    if (!projectId || !currentUser?.id) return false

    // Проверяем права на страницу устройств или на проект
    const pagePermission = allPermissions.find(
      (p) =>
        p.project_id === Number(projectId) &&
        (p.resource_type === 'page:devices' || p.resource_type === 'page:device-instances') &&
        p.resource_id === null &&
        p.permission !== 'deny' &&
        (p.permission === 'edit' || p.permission === 'manage')
    )

    if (pagePermission) return true

    // Проверяем наследование от роли проекта
    const access = projectAccessData?.access?.find(
      (a) => a.project_id === Number(projectId),
    )
    if (access && access.role_in_project) {
      return ['editor', 'project_admin'].includes(access.role_in_project)
    }

    return false
  }, [currentUser, projectId, allPermissions, projectAccessData])

  const { data: project, isLoading: projectLoading } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => getProject(Number(projectId)),
    enabled: !!projectId,
  })

  const { data: panel } = useQuery({
    queryKey: ['electricalPanel', panelId],
    queryFn: () => getElectricalPanelById(Number(panelId!)),
    enabled: !!panelId,
  })

  const { data: device, isLoading: deviceLoading, error: deviceError } = useQuery({
    queryKey: ['device', deviceId],
    queryFn: () => getSpecificationItem(Number(deviceId!)),
    enabled: !!deviceId,
  })

  const { data: contacts = [], isLoading: contactsLoading } = useQuery({
    queryKey: ['deviceContacts', deviceId],
    queryFn: () => getDeviceContacts(Number(deviceId!)),
    enabled: !!deviceId,
  })

  const queryClient = useQueryClient()

  // Загрузка интерфейсов спецификации
  const { data: interfaces = [], isLoading: interfacesLoading } = useQuery({
    queryKey: ['specificationInterfaces', deviceId],
    queryFn: () => getSpecificationInterfaces(Number(deviceId!)),
    enabled: !!deviceId,
  })

  // Загрузка типов интерфейсов
  const { data: interfaceTypes = [] } = useQuery({
    queryKey: ['commInterfaceTypes'],
    queryFn: getCommInterfaceTypes,
  })

  // Состояние формы интерфейсов
  const [showInterfaceModal, setShowInterfaceModal] = useState(false)
  const [editingInterface, setEditingInterface] = useState<SpecificationInterface | null>(null)
  const [interfaceForm, setInterfaceForm] = useState({
    interface_type_id: 0,
    chassis_label: '',
    os_name: ''
  })

  // Мутации для интерфейсов
  const createInterfaceMutation = useMutation({
    mutationFn: createSpecificationInterface,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['specificationInterfaces', deviceId] })
      setShowInterfaceModal(false)
      setInterfaceForm({ interface_type_id: 0, chassis_label: '', os_name: '' })
    },
    onError: (err: any) => {
      console.error(err)
      alert('Ошибка при создании интерфейса: ' + (err?.response?.data?.error || err.message))
    }
  })

  const updateInterfaceMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<SpecificationInterface> }) =>
      updateSpecificationInterface(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['specificationInterfaces', deviceId] })
      setShowInterfaceModal(false)
      setEditingInterface(null)
      setInterfaceForm({ interface_type_id: 0, chassis_label: '', os_name: '' })
    },
    onError: (err: any) => {
      console.error(err)
      alert('Ошибка при обновлении интерфейса: ' + (err?.response?.data?.error || err.message))
    }
  })

  const deleteInterfaceMutation = useMutation({
    mutationFn: deleteSpecificationInterface,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['specificationInterfaces', deviceId] })
    },
    onError: (err: any) => {
      console.error(err)
      alert('Ошибка при удалении: ' + (err?.response?.data?.error || err.message))
    }
  })

  const handleOpenAddInterface = () => {
    setEditingInterface(null)
    setInterfaceForm({
      interface_type_id: interfaceForm.interface_type_id || interfaceTypes[0]?.id || 0,
      chassis_label: '',
      os_name: ''
    })
    setShowInterfaceModal(true)
  }

  const handleOpenEditInterface = (item: SpecificationInterface) => {
    setEditingInterface(item)
    setInterfaceForm({
      interface_type_id: item.interface_type_id,
      chassis_label: item.chassis_label,
      os_name: item.os_name || ''
    })
    setShowInterfaceModal(true)
  }

  const handleDeleteInterface = (id: number, label: string) => {
    if (confirm(`Вы уверены, что хотите удалить интерфейс "${label}"?`)) {
      deleteInterfaceMutation.mutate(id)
    }
  }

  const handleInterfaceSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!interfaceForm.interface_type_id) {
      alert('Выберите тип интерфейса')
      return
    }
    if (!interfaceForm.chassis_label.trim()) {
      alert('Введите надпись на корпусе')
      return
    }

    if (editingInterface) {
      updateInterfaceMutation.mutate({
        id: editingInterface.id,
        data: {
          interface_type_id: interfaceForm.interface_type_id,
          chassis_label: interfaceForm.chassis_label.trim(),
          os_name: interfaceForm.os_name.trim() || undefined
        }
      })
    } else {
      createInterfaceMutation.mutate({
        specification_id: Number(deviceId!),
        interface_type_id: interfaceForm.interface_type_id,
        chassis_label: interfaceForm.chassis_label.trim(),
        os_name: interfaceForm.os_name.trim() || undefined
      })
    }
  }

  if (projectLoading || deviceLoading) {
    return <div className="text-center py-8">Загрузка...</div>
  }

  if (!project) {
    return <div className="text-center py-8 text-red-600">Проект не найден</div>
  }

  if (deviceError || !device) {
    return (
      <div className="text-center py-8 text-red-600">
        Устройство не найдено
        <div className="mt-4">
          <Link 
            to={panelId ? `/projects/${projectId}/panels/${panelId}/devices` : `/projects/${projectId}/devices`}
            className="text-blue-600 hover:text-blue-800"
          >
            ← Вернуться к списку устройств
          </Link>
        </div>
      </div>
    )
  }

  // Сортируем контакты по номеру
  const sortedContacts = [...contacts].sort((a, b) => {
    const numA = parseInt(String(a.contact_number).replace(/\D/g, '')) || 0
    const numB = parseInt(String(b.contact_number).replace(/\D/g, '')) || 0
    return numA - numB
  })

  // Цветовые коды
  const CONTACT_COLORS: Record<string, { label: string; bg: string; text: string }> = {
    WH: { label: 'Белый', bg: 'bg-white', text: 'text-gray-900' },
    BU: { label: 'Синий', bg: 'bg-blue-500', text: 'text-white' },
    BN: { label: 'Коричневый', bg: 'bg-amber-800', text: 'text-white' },
    GY: { label: 'Серый', bg: 'bg-gray-500', text: 'text-white' },
    GN: { label: 'Зеленый', bg: 'bg-green-500', text: 'text-white' },
    YE: { label: 'Желтый', bg: 'bg-yellow-400', text: 'text-gray-900' },
    RD: { label: 'Красный', bg: 'bg-red-500', text: 'text-white' },
    OG: { label: 'Оранжевый', bg: 'bg-orange-500', text: 'text-white' },
    PK: { label: 'Розовый', bg: 'bg-pink-400', text: 'text-gray-900' },
    VT: { label: 'Фиолетовый', bg: 'bg-purple-500', text: 'text-white' },
    GNYE: { label: 'Желто-зеленый', bg: 'bg-green-400', text: 'text-gray-900' },
    BK: { label: 'Черный', bg: 'bg-gray-900', text: 'text-white' },
  }

  const getColorInfo = (colorCode: string) => {
    return CONTACT_COLORS[colorCode] || { label: colorCode, bg: 'bg-gray-300', text: 'text-gray-900' }
  }

  const getPositionLabel = (position: string | null) => {
    if (!position) return '—'
    const labels: Record<string, string> = {
      top: '⬆️ Верхний',
      bottom: '⬇️ Нижний',
      left: '⬅️ Левый',
      right: '➡️ Правый'
    }
    return labels[position] || position
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <Link to={`/projects/${projectId}`} className="text-blue-600 hover:text-blue-800 mb-2 inline-block">
            ← Назад к {project.name}
          </Link>
          <Link 
            to={panelId ? `/projects/${projectId}/panels/${panelId}/devices` : `/projects/${projectId}/devices`}
            className="text-blue-600 hover:text-blue-800 mb-2 ml-4 inline-block"
          >
            ← К списку устройств
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">
            Элемент: {device.name}
            {panel && (
              <span className="text-lg font-normal text-gray-600 ml-2">
                → {panel.proj_id} ({panel.name})
              </span>
            )}
          </h1>
          <p className="text-gray-600 mt-1">
            Просмотр информации об устройстве и его экземплярах
          </p>
        </div>
        {canEdit && (
          <button
            onClick={() => setShowEditModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            ✏️ Редактировать
          </button>
        )}
      </div>

      {/* Device Information */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Информация об устройстве</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="text-sm text-gray-500">ID</div>
            <div className="text-lg font-medium text-gray-900">#{device.id}</div>
          </div>
          {device.manufacturer && (
            <div>
              <div className="text-sm text-gray-500">Производитель</div>
              <div className="text-lg font-medium text-gray-900">{device.manufacturer}</div>
            </div>
          )}
          {device.model && (
            <div>
              <div className="text-sm text-gray-500">Модель</div>
              <div className="text-lg font-medium text-gray-900">{device.model}</div>
            </div>
          )}
          {device.device_type_name && (
            <div>
              <div className="text-sm text-gray-500">Тип устройства</div>
              <div className="text-lg font-medium text-gray-900">{device.device_type_name}</div>
            </div>
          )}
          {device.power_consumption && (
            <div>
              <div className="text-sm text-gray-500">Потребляемая мощность</div>
              <div className="text-lg font-medium text-gray-900">{device.power_consumption} Вт</div>
            </div>
          )}
          {device.unit_price && (
            <div>
              <div className="text-sm text-gray-500">Цена за единицу</div>
              <div className="text-lg font-medium text-gray-900">{device.unit_price} ₽</div>
            </div>
          )}
          {device.width && device.height && device.depth && (
            <>
              <div>
                <div className="text-sm text-gray-500">Ширина</div>
                <div className="text-lg font-medium text-gray-900">{device.width} мм</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Высота</div>
                <div className="text-lg font-medium text-gray-900">{device.height} мм</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Глубина</div>
                <div className="text-lg font-medium text-gray-900">{device.depth} мм</div>
              </div>
            </>
          )}
          {device.description && (
            <div className="md:col-span-2">
              <div className="text-sm text-gray-500">Описание</div>
              <div className="text-lg font-medium text-gray-900">{device.description}</div>
            </div>
          )}
          {device.documentation_url && (
            <div>
              <div className="text-sm text-gray-500">Документация</div>
              <a 
                href={device.documentation_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 underline"
              >
                Открыть документацию
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Contacts Information */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Контакты устройства</h2>
        {contactsLoading ? (
          <div className="text-center py-4 text-gray-500">Загрузка контактов...</div>
        ) : sortedContacts.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    № контакта
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Маркировка
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Позиция
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Цвет
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Визуально
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedContacts.map((contact) => {
                  const colorCode = contact.color_code || ''
                  const colorInfo = getColorInfo(colorCode)
                  return (
                    <tr key={contact.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {contact.contact_number}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {contact.contact_label || '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {getPositionLabel(contact.contact_position)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {colorCode ? `${colorCode} - ${colorInfo.label}` : '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {colorCode ? (
                          <div 
                            className={`w-8 h-8 rounded border-2 border-gray-300 ${colorInfo.bg} ${colorInfo.text} flex items-center justify-center text-xs font-bold`}
                            title={`${colorCode} - ${colorInfo.label}`}
                          >
                            {colorCode}
                          </div>
                        ) : (
                          <div className="w-8 h-8 rounded border-2 border-gray-300 bg-gray-100 flex items-center justify-center text-xs text-gray-400">
                            —
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            У устройства нет настроенных контактов
          </div>
        )}
      </div>

      {/* Interfaces Information */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Интерфейсы устройства</h2>
          {canEdit && (
            <button
              onClick={handleOpenAddInterface}
              className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition-colors duration-150 flex items-center gap-1.5"
            >
              <span>➕ Добавить интерфейс</span>
            </button>
          )}
        </div>

        {interfacesLoading ? (
          <div className="text-center py-4 text-gray-500">Загрузка интерфейсов...</div>
        ) : interfaces.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Надпись на корпусе
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Имя в ОС
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Тип интерфейса
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Категория
                  </th>
                  {canEdit && (
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Действия
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {interfaces.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                      {item.chassis_label}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {item.os_name ? (
                        <code className="text-xs bg-gray-100 border border-gray-200 px-2 py-0.5 rounded text-gray-800 font-mono">
                          {item.os_name}
                        </code>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.interface_type_name || 'Неизвестно'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {item.interface_type_category ? (
                        <span className="px-2 py-0.5 text-xs font-semibold bg-gray-100 text-gray-800 rounded-full capitalize">
                          {item.interface_type_category}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    {canEdit && (
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleOpenEditInterface(item)}
                            className="p-1 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                            title="Редактировать"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => handleDeleteInterface(item.id, item.chassis_label)}
                            className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                            title="Удалить"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-300">
            У устройства нет настроенных интерфейсов
          </div>
        )}
      </div>

      {/* Device Instances or Cable Instances */}
      {device.device_type_code === 'cable' ? (
        <CableInstances 
          projectId={Number(projectId!)} 
          electricalPanelId={panelId ? Number(panelId) : undefined}
          specificationId={Number(deviceId!)}
          hideCableColumn={true}
        />
      ) : (
        <DeviceInstances 
          projectId={Number(projectId!)} 
          electricalPanelId={panelId ? Number(panelId) : undefined}
          specificationId={Number(deviceId!)}
          hideParentColumn={true}
        />
      )}

      {/* Edit Modal */}
      {device && (
        <DeviceSpecEditModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          selectedDevice={device}
          projectId={Number(projectId!)}
          onSuccess={() => {
            // Данные обновятся автоматически через query invalidation в модальном окне
          }}
        />
      )}

      {/* Interface Add/Edit Modal */}
      {showInterfaceModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowInterfaceModal(false)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-blue-600 px-6 py-4 text-white flex justify-between items-center">
              <h3 className="text-lg font-bold">
                {editingInterface ? 'Редактировать интерфейс' : 'Добавить интерфейс'}
              </h3>
              <button 
                onClick={() => setShowInterfaceModal(false)} 
                className="text-white hover:text-gray-200 text-xl font-bold"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleInterfaceSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Тип интерфейса *</label>
                <select
                  value={interfaceForm.interface_type_id}
                  onChange={(e) => setInterfaceForm({ ...interfaceForm, interface_type_id: Number(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value={0} disabled>Выберите тип интерфейса</option>
                  {interfaceTypes.map((type) => {
                    const detailKindLabels: Record<string, string> = {
                      lan: 'LAN',
                      modbus: 'Modbus',
                      audio: 'Audio',
                      generic: 'Generic'
                    }
                    const detailLabel = detailKindLabels[type.detail_kind] || type.detail_kind
                    return (
                      <option key={type.id} value={type.id}>
                        {type.name} ({type.category}) ({detailLabel})
                      </option>
                    )
                  })}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Надпись на корпусе (label) *</label>
                <input
                  type="text"
                  required
                  value={interfaceForm.chassis_label}
                  onChange={(e) => setInterfaceForm({ ...interfaceForm, chassis_label: e.target.value })}
                  placeholder="Например: WAN, LAN1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Имя в операционной системе</label>
                <input
                  type="text"
                  value={interfaceForm.os_name}
                  onChange={(e) => setInterfaceForm({ ...interfaceForm, os_name: e.target.value })}
                  placeholder="Например: eth0, eth1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
                />
              </div>

              <div className="flex space-x-3 pt-4 border-t border-gray-100 justify-end">
                <button
                  type="button"
                  onClick={() => setShowInterfaceModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  disabled={createInterfaceMutation.isPending || updateInterfaceMutation.isPending}
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold shadow disabled:opacity-50"
                >
                  {createInterfaceMutation.isPending || updateInterfaceMutation.isPending ? 'Сохранение...' : 'Сохранить'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <SpecificationDocumentsSection
        specificationId={Number(deviceId!)}
        canEdit={canEdit}
      />

      <PostsSection
        entityType="device"
        entityId={Number(deviceId!)}
        canEdit={canEdit}
      />
    </div>
  )
}

